import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} dirty - The potentially unsafe HTML string
 * @param {Object} options - DOMPurify configuration options
 * @returns {string} - Sanitized HTML string
 */
export const sanitizeHtml = (dirty, options = {}) => {
  if (!dirty || typeof dirty !== 'string') {
    return '';
  }

  const defaultOptions = {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['href', 'title'],
    ALLOW_DATA_ATTR: false,
    ...options,
  };

  return DOMPurify.sanitize(dirty, defaultOptions);
};

/**
 * Sanitize plain text input by removing all HTML tags
 * @param {string} input - The input string to sanitize
 * @returns {string} - Plain text with HTML stripped
 */
export const sanitizeText = input => {
  if (!input || typeof input !== 'string') {
    return '';
  }

  return DOMPurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

/**
 * Sanitize user input for search queries
 * @param {string} query - The search query to sanitize
 * @returns {string} - Sanitized search query
 */
export const sanitizeSearchQuery = query => {
  if (!query || typeof query !== 'string') {
    return '';
  }

  // Simple sanitization that preserves spaces - just remove dangerous characters
  const sanitized = query
    .replace(/[<>"'&]/g, '') // Remove potentially dangerous characters
    .replace(/\s+/g, ' ') // Normalize whitespace but keep spaces
    .trim();

  return sanitized.substring(0, 200); // Limit search query length
};

/**
 * Sanitize form input data
 * @param {Object} data - Object containing form data to sanitize
 * @param {Array} textFields - Array of field names to treat as plain text
 * @param {Array} htmlFields - Array of field names to treat as HTML (with limited tags)
 * @returns {Object} - Sanitized form data
 */
export const sanitizeFormData = (data, textFields = [], htmlFields = []) => {
  if (!data || typeof data !== 'object') {
    return {};
  }

  const sanitized = { ...data };

  // Sanitize text fields
  textFields.forEach(field => {
    if (sanitized[field]) {
      sanitized[field] = sanitizeText(sanitized[field]);
    }
  });

  // Sanitize HTML fields
  htmlFields.forEach(field => {
    if (sanitized[field]) {
      sanitized[field] = sanitizeHtml(sanitized[field]);
    }
  });

  return sanitized;
};

/**
 * Validate and sanitize email input
 * @param {string} email - Email to validate and sanitize
 * @returns {string} - Sanitized email or empty string if invalid
 */
export const sanitizeEmail = email => {
  if (!email || typeof email !== 'string') {
    return '';
  }

  // Just sanitize the text but don't validate format - let user type
  return sanitizeText(email.toLowerCase().trim());
};

/**
 * Rate limiting helper (client-side basic protection)
 * @param {string} key - Unique key for the action
 * @param {number} limit - Maximum attempts
 * @param {number} windowMs - Time window in milliseconds
 * @returns {boolean} - Whether the action is allowed
 */
export const checkRateLimit = (key, limit = 10, windowMs = 60000) => {
  const now = Date.now();
  const storageKey = `rate_limit_${key}`;

  try {
    const stored = sessionStorage.getItem(storageKey);
    const attempts = stored ? JSON.parse(stored) : [];

    // Remove old attempts outside the window
    const validAttempts = attempts.filter(timestamp => now - timestamp < windowMs);

    if (validAttempts.length >= limit) {
      return false; // Rate limit exceeded
    }

    // Add current attempt
    validAttempts.push(now);
    sessionStorage.setItem(storageKey, JSON.stringify(validAttempts));

    return true;
  } catch (error) {
    console.error('Rate limiting error:', error);
    return true; // Fail open in case of errors
  }
};

/**
 * Generate a secure random string for CSRF tokens (client-side)
 * @param {number} length - Length of the token
 * @returns {string} - Random token string
 */
export const generateCSRFToken = (length = 32) => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Sanitize URL parameters to prevent injection
 * @param {string} param - URL parameter to sanitize
 * @returns {string} - Sanitized parameter
 */
export const sanitizeUrlParam = param => {
  if (!param || typeof param !== 'string') {
    return '';
  }

  // Remove potentially dangerous characters
  return param.replace(/[<>"'&]/g, '').trim();
};

const SecurityUtils = {
  sanitizeHtml,
  sanitizeText,
  sanitizeSearchQuery,
  sanitizeFormData,
  sanitizeEmail,
  checkRateLimit,
  generateCSRFToken,
  sanitizeUrlParam,
};

export default SecurityUtils;
